import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { Box, Button, Container, Typography, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { TextInput } from '../../shared/textInput/textInput';
import { ImageUploader } from '../../shared/ImageUploader/imageUploader';
import { branchValidationSchema } from '../../../validationSchema/branchValidationSchema';
import { Branch } from '../../../interfaces/entities/branch';
import { AutoCompleteInput } from '../../shared/autoComplete/autoCompleteInput';

interface BranchFormProps {
  title: string;
  initialValues: Branch;
  onSubmit: (values: any) => Promise<void>;
  onImageChange: (file: File | null) => void;
  onCheckUrlKey: (urlKey: string) => Promise<boolean>;
  companyOptions: { id: string; label: string }[];
  isSubmitting?: boolean;
  onCancel?: () => void;
}

export const BranchForm: React.FC<BranchFormProps> = ({ initialValues, title, onSubmit, onImageChange, onCheckUrlKey, companyOptions, isSubmitting = false, onCancel }) => {
  const [urlKeyStatus, setUrlKeyStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  const handleCheckUrlKey = async (urlKey: string) => {
    if (!urlKey) return;
    setUrlKeyStatus('checking');
    const available = await onCheckUrlKey(urlKey);
    setUrlKeyStatus(available ? 'available' : 'taken');
  };

  return (
    <Box>
      <Container maxWidth="sm" className="branch-form-container">
        <div style={{ padding: '1rem', borderRadius: '8px' }}>
          <Typography variant='h5'>{title}</Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={branchValidationSchema}
            onSubmit={onSubmit}
            validateOnMount={true}
          >

            {({ values, isValid, errors, touched, setFieldValue }) => (
              <Form>
                <AutoCompleteInput
                  label="Company"
                  onChange={setFieldValue}
                  options={companyOptions}
                  required
                  name={'companyId'}
                  disabled={true}
                />

                <TextInput
                  label="Branch Name"
                  name="name"
                  errorText={errors.name && touched.name ? errors.name : ""}
                  disabled={isSubmitting}
                />

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextInput
                      label="URL Key"
                      name="urlKey"
                      errorText={errors.urlKey && touched.urlKey ? errors.urlKey : ""}
                      disabled={isSubmitting}
                      onChange={() => setUrlKeyStatus('idle')}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => handleCheckUrlKey(values.urlKey ?? '')}
                    disabled={!values.urlKey || urlKeyStatus === 'checking' || isSubmitting}
                    sx={{ mt: '16px', height: '56px', minWidth: '90px', whiteSpace: 'nowrap' }}
                  >
                    {urlKeyStatus === 'checking'
                      ? <CircularProgress size={18} />
                      : 'Check'}
                  </Button>
                </Box>

                {urlKeyStatus === 'available' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: -1, mb: 1 }}>
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                    <Typography variant="caption" color="success.main">URL Key is available</Typography>
                  </Box>
                )}
                
                {urlKeyStatus === 'taken' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: -1, mb: 1 }}>
                    <ErrorOutlineIcon fontSize="small" color="error" />
                    <Typography variant="caption" color="error.main">URL Key is already taken</Typography>
                  </Box>
                )}

                <TextInput
                  label="Contact Number"
                  name="phone"
                  errorText={errors.phone && touched.phone ? errors.phone : ""}
                  disabled={isSubmitting}
                />
                <TextInput
                  label="Address"
                  name="address"
                  errorText={errors.address && touched.address ? errors.address : ""}
                  disabled={isSubmitting}
                />
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Front side photo of the branch
                </Typography>
                <ImageUploader
                  name="image"
                  uploadedUrl={values.image}
                  onChange={onImageChange}
                  disabled={isSubmitting}
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isValid || isSubmitting}
                    fullWidth
                    className="register-button"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onCancel}
                    disabled={isSubmitting}
                    fullWidth
                  >
                    Cancel
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </Container>
    </Box>
  );
};
